import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Upload, Shield, Eye, Calendar, DollarSign } from 'lucide-react';
import AdSlotBuyButton from '../components/AdSlotBuyButton';

const API = process.env.REACT_APP_BACKEND_URL;

const PLACEMENT_LABEL = {
  top_banner: { label: 'Top Rotating Banner', visibility: 'Every page', position: 'Above radio bar' },
  homepage_hero: { label: 'Homepage Hero Banner', visibility: 'Homepage only', position: 'Below hero section' },
  beats_header: { label: 'Beats Page Header', visibility: 'Beats page', position: 'Above beats grid' },
  books_sponsored: { label: 'Sponsored Book Card', visibility: 'PDF Books & More', position: 'Native in book grid' },
};

const AdvertisePage = () => {
  const [slots, setSlots] = useState([]);
  const formRef = useRef(null);
  const [form, setForm] = useState({
    brand_name: '',
    contact_name: '',
    contact_email: '',
    ad_slot_id: '',
    start_date: '',
    end_date: '',
    destination_url: '',
    headline: '',
    short_description: '',
    cta_text: '',
    notes: '',
    budget: '',
  });
  const [creativeFile, setCreativeFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/api/ads/slots`, { params: { is_active: true } })
      .then((res) => setSlots(res.data || []))
      .catch(() => {});
  }, []);

  const scrollToForm = (slotId) => {
    if (slotId) setForm((f) => ({ ...f, ad_slot_id: slotId }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);
    if (!form.ad_slot_id) {
      setError('Please choose a placement.');
      return;
    }
    setSubmitting(true);
    try {
      let ad_image_url = '';
      if (creativeFile) {
        // Phase 1 — JPG/PNG only
        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(creativeFile.type)) {
          setError('Only JPG and PNG images are accepted for ad creatives.');
          setSubmitting(false);
          return;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append('file', creativeFile);
        const upRes = await axios.post(`${API}/api/ads/upload-creative`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ad_image_url = upRes.data?.url || '';
        setUploading(false);
      }
      const payload = {
        ad_slot_id: form.ad_slot_id,
        brand_name: form.brand_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        ad_image_url,
        destination_url: form.destination_url,
        headline: form.headline,
        short_description: form.short_description,
        cta_text: form.cta_text,
        start_date: form.start_date,
        end_date: form.end_date,
        notes: form.notes
          ? `${form.notes}${form.budget ? `\n\nBudget: ${form.budget}` : ''}`
          : (form.budget ? `Budget: ${form.budget}` : ''),
      };
      const res = await axios.post(`${API}/api/ads/bookings`, payload);
      const newBooking = res.data;
      // Immediately try to start Stripe checkout (advertiser pays first)
      try {
        const { data: ck } = await axios.post(`${API}/api/ads/checkout`, {
          booking_id: newBooking.booking_id || newBooking.id,
          origin_url: window.location.origin,
        });
        if (ck?.checkout_url) {
          // Redirect to Stripe — they'll come back to /advertise/payment-success
          window.location.href = ck.checkout_url;
          return;
        }
      } catch (payErr) {
        // Soft-fail: show success state with note about manual payment fallback
        console.warn('Stripe redirect failed, falling back to manual payment notice', payErr);
      }
      // Fallback path — booking exists but couldn't auto-redirect
      setSuccess(newBooking);
      setForm({
        brand_name: '', contact_name: '', contact_email: '', ad_slot_id: '',
        start_date: '', end_date: '', destination_url: '',
        headline: '', short_description: '', cta_text: '', notes: '', budget: '',
      });
      setCreativeFile(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-20" data-testid="advertise-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">
          Media Kit · Brand Partnerships
        </p>
        <h1 className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-wider mb-6 text-white">
          Advertise With <span className="text-[#D4AF37]">IAMHETHATIS</span>
        </h1>
        <p className="text-base sm:text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-10 leading-relaxed">
          Reach a focused audience that comes here for music, instrumentals, books, and culture.
          Limited, premium placements only — every advertiser is hand-approved.
        </p>
        <button
          onClick={() => scrollToForm()}
          data-testid="advertise-cta-book"
          className="px-8 py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors"
        >
          Book a Placement
        </button>
      </section>

      {/* Why advertise */}
      <section className="bg-[#0F0F0F] border-y border-[#262626]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-['Bebas_Neue'] text-3xl tracking-wider mb-10 text-center">
            Why Advertise Here
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Eye, title: 'Engaged Audience', body: 'Visitors come here intentionally — for music, beats, books, and merch — not to scroll past content.' },
              { icon: Shield, title: 'Brand-Safe', body: 'No adult, gambling, scam, or misleading content. Every advertiser is hand-approved by the artist.' },
              { icon: CheckCircle2, title: 'Cultural Fit', body: 'Independent music brand built on integrity, faith, and craft. Your message lands in the right room.' },
              { icon: Calendar, title: 'Flexible Terms', body: 'Monthly bookings. Pause, swap, or extend campaigns based on performance.' },
              { icon: DollarSign, title: 'Transparent Pricing', body: 'Flat monthly rates. No bidding, no hidden fees, no auction games.' },
              { icon: Upload, title: 'You Own The Creative', body: 'Upload your own banner, link to your destination, and we handle distribution.' },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-[#1A1A1A] border border-[#262626] p-6 rounded-lg">
                <Icon size={22} className="text-[#D4AF37] mb-3" />
                <h3 className="text-white font-medium mb-2">{title}</h3>
                <p className="text-sm text-[#A3A3A3] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available placements */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-['Bebas_Neue'] text-3xl tracking-wider mb-2 text-center">
          Available Placements
        </h2>
        <p className="text-center text-[#A3A3A3] text-sm mb-10">
          A small, curated set of placements — refined over time, not overcrowded.
        </p>
        <div className="grid sm:grid-cols-2 gap-6">
          {slots.length === 0 && (
            <p className="col-span-full text-center text-[#737373]">Loading placements...</p>
          )}
          {slots.map((slot) => {
            const meta = PLACEMENT_LABEL[slot.placement] || {};
            return (
              <div
                key={slot.id || slot.slot_id}
                data-testid={`ad-slot-card-${slot.placement}`}
                className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 flex flex-col hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-['Bebas_Neue'] text-xl tracking-wider text-white">
                    {meta.label || slot.slot_name}
                  </h3>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded">
                    {slot.dimensions}
                  </span>
                </div>
                <p className="text-sm text-[#A3A3A3] mb-4 flex-1 leading-relaxed">
                  {slot.description}
                </p>
                <div className="space-y-1 text-xs text-[#737373] mb-4">
                  <div><span className="text-white">Visibility:</span> {meta.visibility || '—'}</div>
                  <div><span className="text-white">Position:</span> {meta.position || '—'}</div>
                  <div><span className="text-white">Slots available:</span> {slot.max_slots}</div>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-[#262626]">
                  <div>
                    <p className="text-2xl text-[#D4AF37] font-medium">
                      ${slot.price_per_month.toFixed(0)}
                      <span className="text-sm text-[#A3A3A3]">/mo</span>
                    </p>
                  </div>
                  <button
                    onClick={() => scrollToForm(slot.id || slot.slot_id)}
                    data-testid={`ad-slot-book-${slot.placement}`}
                    className="px-4 py-2 bg-transparent text-[#D4AF37] border border-[#D4AF37]/40 text-sm font-medium uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors rounded"
                  >
                    Book with creative
                  </button>
                </div>
                {/* Instant-purchase path via Shopify — advertiser pays first,
                    admin follows up for creative. Falls back to null when the
                    slot isn't mapped so the booking form remains the only
                    path. */}
                <AdSlotBuyButton slot={slot} />
              </div>
            );
          })}
        </div>
      </section>

      {/* Creative guidelines */}
      <section className="bg-[#0F0F0F] border-y border-[#262626]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-['Bebas_Neue'] text-3xl tracking-wider mb-8 text-center">
            Creative Guidelines
          </h2>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl mx-auto text-sm text-[#A3A3A3]">
            {[
              ['Accepted file types', 'JPG, PNG, WebP'],
              ['Max file size', '10 MB'],
              ['Recommended format', 'High-resolution, optimized for retina'],
              ['Destination URL', 'HTTPS only — must match the brand'],
              ['Approval', 'Final approval rests with admin (typically <48h)'],
              ['Not allowed', 'Adult, gambling, scam, malware, hate speech, misleading claims'],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-4 py-2 border-b border-[#1A1A1A]">
                <span className="text-white shrink-0 w-44">{k}</span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section ref={formRef} className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-['Bebas_Neue'] text-3xl tracking-wider mb-2 text-center">
          Submit Your Booking
        </h2>
        <p className="text-center text-[#A3A3A3] text-sm mb-2 max-w-xl mx-auto">
          Pay first, then your booking enters manual review. If your campaign isn&rsquo;t approved, a full refund is issued automatically. Nothing goes live without admin approval.
        </p>
        <p className="text-center text-[#737373] text-xs mb-10">
          Charge → Review → Live. Refund only if rejected.
        </p>

        {success ? (
          <div className="bg-[#0A2216] border border-[#D4AF37]/40 rounded-lg p-8 text-center" data-testid="advertise-success">
            <CheckCircle2 size={40} className="text-[#D4AF37] mx-auto mb-4" />
            <h3 className="text-xl text-white font-medium mb-2">Submitted!</h3>
            <p className="text-[#A3A3A3] mb-2">{success.message}</p>
            <p className="text-sm text-[#737373] mb-1">
              Estimated total: <span className="text-[#D4AF37]">${success.total_price?.toFixed(2)}</span> ({success.duration_months} month{success.duration_months !== 1 ? 's' : ''})
            </p>
            <p className="text-xs text-[#737373] mt-3 leading-relaxed">
              We couldn't auto-redirect you to payment. Please email <a href="mailto:admin@iamhethatis.com" className="text-[#D4AF37] underline">admin@iamhethatis.com</a> with your booking ID to complete payment manually.
            </p>
            <button
              onClick={() => setSuccess(null)}
              className="mt-6 text-sm text-[#D4AF37] underline"
            >
              Submit another booking
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 sm:p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Brand name *" testid="ad-form-brand">
                <input required value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
              <Field label="Contact name *" testid="ad-form-contact-name">
                <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
              <Field label="Contact email *" testid="ad-form-email">
                <input required type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
              <Field label="Budget (optional)" testid="ad-form-budget">
                <input value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="$200"
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
            </div>

            <Field label="Desired placement *" testid="ad-form-slot">
              <select required value={form.ad_slot_id} onChange={(e) => setForm({ ...form, ad_slot_id: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none">
                <option value="">Choose a placement...</option>
                {slots.map((s) => (
                  <option key={s.id || s.slot_id} value={s.id || s.slot_id}>
                    {(PLACEMENT_LABEL[s.placement] || {}).label || s.slot_name} — ${s.price_per_month}/mo
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Start date *" testid="ad-form-start">
                <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
              <Field label="End date *" testid="ad-form-end">
                <input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
              </Field>
            </div>

            <Field label="Destination URL *" testid="ad-form-url">
              <input required type="url" placeholder="https://..." value={form.destination_url} onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
            </Field>

            {/* Ad creative content */}
            <div className="border-t border-[#262626] pt-5 mt-5">
              <h3 className="text-sm uppercase tracking-[0.2em] text-[#D4AF37] mb-1">Ad Creative</h3>
              <p className="text-xs text-[#737373] mb-4">Keep it visual and to-the-point. A strong image, a short headline, one supporting line, and a clear CTA.</p>
            </div>

            <Field label="Headline (max 60 chars)" testid="ad-form-headline">
              <input maxLength={60} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
                placeholder="e.g., Premium Beats from Top Producers"
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
            </Field>

            <Field label="Short description (max 120 chars)" testid="ad-form-short-desc">
              <input maxLength={120} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })}
                placeholder="One supporting line that explains the offer."
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
            </Field>

            <Field label="CTA button text (max 20 chars)" testid="ad-form-cta">
              <input maxLength={20} value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                placeholder='e.g., "Shop Now", "Learn More"'
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
            </Field>

            <Field label="Creative upload (JPG / PNG only) *" testid="ad-form-creative">
              <input type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={(e) => setCreativeFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-[#A3A3A3] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#D4AF37] file:text-black file:font-medium hover:file:bg-[#E5C048]" />
              {creativeFile && <p className="text-xs text-[#D4AF37] mt-2">{creativeFile.name}</p>}
              <p className="text-xs text-[#737373] mt-2">No video for now. Phase 1 supports JPG/PNG only.</p>
            </Field>

            <Field label="Notes / instructions for admin" testid="ad-form-notes">
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Anything you'd like the artist to know before approving..."
                className="w-full bg-[#0F0F0F] border border-[#262626] rounded px-4 py-3 text-white focus:border-[#D4AF37] outline-none" />
            </Field>

            {error && (
              <p className="text-red-500 text-sm" data-testid="ad-form-error">{error}</p>
            )}

            <button type="submit" disabled={submitting}
              data-testid="ad-form-submit"
              className="w-full py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50">
              {submitting ? (uploading ? 'Uploading creative...' : 'Submitting...') : 'Continue to Secure Payment'}
            </button>

            <p className="text-xs text-center text-[#737373]">
              By submitting you agree to the creative guidelines above. Final approval rests with admin.
            </p>
          </form>
        )}
      </section>

      <div className="text-center pb-12">
        <Link to="/" className="text-sm text-[#737373] hover:text-[#D4AF37] underline">
          &larr; Back to homepage
        </Link>
      </div>
    </div>
  );
};

const Field = ({ label, testid, children }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">{label}</span>
    {children}
  </label>
);

export default AdvertisePage;
